import {
  ElementRef,
  Ref,
  forwardRef,
  useRef,
  useState,
  useCallback,
  ReactElement,
} from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import {
  Checkbox,
  IconButton,
  ListSubheader,
  MenuItem,
  Select as MuiSelect,
  SelectProps,
  styled,
} from '@mui/material';

import { IcArrowDown } from '@/assets/images';
import { colors } from '@/theme/variables';

import { Input } from '../Input';
import { BaseFieldProps } from '../types';

const Root = styled('div')(() => ({
  width: '100%',
  position: 'relative',
  '& .select-clear-icon-button': {
    opacity: 0,
    visibility: 'hidden',
    pointerEvents: 'none',
    transition: 'opacity 120ms ease',
  },
  '&:hover .select-clear-icon-button, &:focus-within .select-clear-icon-button':
    {
      opacity: 1,
      visibility: 'visible',
      pointerEvents: 'auto',
    },
}));

const MenuItemRoot = styled(MenuItem)(({ theme }) => ({
  justifyContent: 'flex-start',
  padding: 9,
  background: theme.palette.background.paper,
  '&.multiple': {
    backgroundColor: 'transparent',
  },

  '& .MuiCheckbox-root': {
    padding: '0 8px 0 0',
  },

  '&.clear-item': {
    color: colors.error.main,
  },
}));

const SubheaderMenuItem = styled(ListSubheader)(() => ({
  fontSize: '16px',
  '& ~ .MuiMenuItem-root': {
    paddingLeft: '25px',
  },
}));

export type SelectItem = {
  label: string | ReactElement;
  value: string | number;
  disabled?: boolean;
  group?: string;
};

interface BaseSelectProps extends Omit<SelectProps, 'error'>, BaseFieldProps {
  items: SelectItem[];
  placeholder?: string;
  multiple?: boolean;
  value: string | number | (string | number)[];
  clearable?: boolean;
  clearButtonVariant?: 'menu-item' | 'icon';
}

export const Select = forwardRef<Ref<any>, BaseSelectProps>((props, ref) => {
  const {
    items,
    label,
    value,
    error,
    multiple,
    autoWidth,
    clearable,
    clearButtonVariant = 'menu-item',
    sx,
    placeholder,
    onChange,
    ...rest
  } = props;

  const [dynamicWidth, setDynamicWidth] = useState<number>(0);
  const refContainer = useRef<ElementRef<'div'>>(null);

  const [minWidth, setMinWidth] = useState<number | null>(null);
  const [openAbove, setOpenAbove] = useState(false);
  const menuCallbackRef = useCallback((menuDiv: HTMLDivElement) => {
    if (menuDiv !== null) {
      if (minWidth === null || minWidth !== menuDiv.clientWidth) {
        setMinWidth(menuDiv.clientWidth);
      }
    }
  }, []);

  const renderValue = (selectedValue: unknown) => {
    if (
      selectedValue === '' ||
      selectedValue === null ||
      (Array.isArray(selectedValue) && selectedValue.length === 0)
    ) {
      return <em style={{ color: '#999' }}>{placeholder || 'None'}</em>;
    }

    if (Array.isArray(selectedValue)) {
      const labels = items.reduce((accumulator, currentItem) => {
        if (
          'value' in currentItem &&
          selectedValue.indexOf(currentItem.value) > -1
        ) {
          accumulator.push(currentItem.label?.toString());
        }
        return accumulator;
      }, [] as string[]);

      return <>{labels.join(', ')}</>;
    } else {
      const selectedItem = items.find(
        (item) => 'value' in item && item.value === selectedValue,
      );
      return (
        <>{selectedItem && 'label' in selectedItem && selectedItem.label}</>
      );
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange(
        {
          target: {
            name: props.name,
            value: multiple ? [] : '',
          },
        } as any,
        null,
      );
    }
  };

  return (
    <Root ref={refContainer} className="selectWrapper">
      <MuiSelect
        input={<Input ref={ref} label={label} error={error} />}
        IconComponent={() => (
          <>
            {clearable &&
              clearButtonVariant === 'icon' &&
              value !== undefined &&
              value !== null &&
              value !== '' && (
                <IconButton
                  className="select-clear-icon-button"
                  size="small"
                  onClick={handleClear}
                  sx={{
                    position: 'absolute',
                    right: 18,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            <IcArrowDown />
          </>
        )}
        value={value ?? (multiple ? [] : '')}
        displayEmpty={!!placeholder}
        renderValue={renderValue}
        className={!value ? 'placeholder' : ''}
        multiple={multiple}
        sx={sx ? sx : minWidth === null ? {} : { minWidth: minWidth + 14 }}
        onOpen={() => {
          if (refContainer.current) {
            if (!autoWidth) {
              setDynamicWidth(refContainer.current.offsetWidth);
            }
            const rect = refContainer.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setOpenAbove(spaceBelow < 250);
          }
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: openAbove ? 'top' : 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: openAbove ? 'bottom' : 'top',
            horizontal: 'left',
          },
          PaperProps: {
            sx: {
              padding: 0,
              maxHeight: 450,
              ...(dynamicWidth > 0 && { width: `${dynamicWidth}px` }),
            },
          },
          keepMounted: true,
          slotProps: { paper: { ref: menuCallbackRef } },
        }}
        onChange={onChange}
        {...rest}
      >
        {items.map((item) => {
          if ('group' in item && item.group) {
            return (
              <SubheaderMenuItem key={`${item.group}`}>
                {item.group}
              </SubheaderMenuItem>
            );
          }
          const currentItem = item as SelectItem;
          return (
            <MenuItemRoot
              key={`${currentItem.label}-${currentItem.value}`}
              value={currentItem.value}
              className={multiple ? 'multiple' : ''}
              disabled={currentItem.disabled}
              sx={{ width: autoWidth ? undefined : `${dynamicWidth}px` }}
              title={currentItem.label?.toString()}
            >
              {multiple && Array.isArray(value) && (
                <Checkbox
                  color="error"
                  checked={value.indexOf(currentItem.value) > -1}
                />
              )}
              {currentItem.label}
            </MenuItemRoot>
          );
        })}

        {clearable && clearButtonVariant === 'menu-item' && !!value && (
          <MenuItemRoot key="clear" value="" className="clear-item">
            Clear selection
          </MenuItemRoot>
        )}
      </MuiSelect>
    </Root>
  );
});

Select.displayName = 'Base Select';
